import { Box, Button, Stack } from "@mui/material"

import React, { useState } from "react"
import { Form } from "@rjsf/mui"
import { IChangeEvent } from "@rjsf/core"
import validator from "@rjsf/validator-ajv8"
import { raffleSchema } from "react-ui-components/components/raffle/raffleSchema"
import { raffleUISchema } from "react-ui-components/components/raffle/raffleUISchema"
import { PopupService } from "react-ui-components/components/PopupService"
import { LoadingService } from "react-ui-components/components/LoadingService"
import { updateDocument } from "../../GenericList"
import { handleUpload } from "../../utils"
import { ImageWidget } from "../CustomWidgets"
import TicketPreview from "../TicketPreview"
import { normalizeTicketDesignForSave } from "./raffleFormUtils"

interface TicketDesignPopupProps {
  raffleId: string
  design?: any
  onSaved?: (design: any) => void
}

/**
 * Standalone editor for a single raffle's ticket design. Saves directly to the
 * raffle document instead of going through the generic list/edit machinery,
 * which expects a real collection + Router context and isn't applicable to a
 * single nested field.
 */
export function TicketDesignPopup({ raffleId, design, onSaved }: TicketDesignPopupProps) {
  const [formData, setFormData] = useState(design || {})

  const handleSubmit = async ({ formData: nextDesign }: IChangeEvent) => {
    try {
      LoadingService.show()
      let resolvedDesign = nextDesign

      if (nextDesign?.ticketImage) {
        resolvedDesign = {
          ...nextDesign,
          ticketImage: await handleUpload({
            image: nextDesign.ticketImage,
            id: raffleId,
            fieldName: "ticketDesign",
            imagesBucket: "raffles",
            prevValue: design?.ticketImage,
          }),
        }
      }

      const normalizedDesign = normalizeTicketDesignForSave(resolvedDesign)

      await updateDocument(["raffles"], { id: raffleId, ticketDesign: normalizedDesign })
      onSaved?.(normalizedDesign)
      PopupService.setPopup(null)
    } catch (error: any) {
      PopupService.pushPopup(error.message)
    } finally {
      LoadingService.hide()
    }
  }

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
      <Box flex={1} minWidth={0}>
        <Form
          schema={raffleSchema.properties.ticketDesign}
          uiSchema={{
            ...raffleUISchema.ticketDesign,
            ticketImage: { "ui:widget": ImageWidget },
          }}
          formData={formData}
          validator={validator}
          onChange={({ formData: next }: IChangeEvent) => setFormData(next)}
          onSubmit={handleSubmit}
        >
          <Button type="submit" variant="contained">
            Save
          </Button>
        </Form>
      </Box>
      <Box flex={1} minWidth={0} display="flex" justifyContent="center" alignItems="flex-start">
        <TicketPreview design={formData} />
      </Box>
    </Stack>
  )
}

export default TicketDesignPopup
